import {useMutation, useQuery} from '@apollo/client';
import {Button, message} from 'antd';
import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {
    hasExceptionOptions,
    isComplaintOptions,
    overallStatusOptions,
    podReviewStatusOptions
} from '../../../../config/filterOptions';
import {MARK_AS_EXCEPTION} from '../../../../graphql/mutations/markAsException';
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {OrderType} from "../../../../graphql/types/OrderType";
import {getUserInfo} from "../../../../utils/auth";
import DateTimeRangeFilterItem from "../../components/DateTimeRangeFilterItem";
import InputFilterItem from "../../components/InputFilterItem";
import ParcelFilterStyle from "../../components/ParcelFilterStyle";
import { t } from '../../../../i18n';
import { getLastWeekDateTimeRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import {
    GENERATE_BATCH_SHIPPING_LABEL_PDF,
    GenerateBatchShippingLabelPdfResponse,
    GenerateBatchShippingLabelPdfVariables
} from "../graphql/mutations/generateShippingLabelPdf";
import {allChannelResponse, GET_ALL_CHANNEL} from "../../../../graphql/queries/getChannelAll";
import {printPdfFromBase64} from "../../../../utils/printUtils";
import {ExceptionDictionaryResponse, GET_ALL_EXCEPTION} from "../graphql/queries/getAllException";
import { parseException, showError } from '../../../../utils/exceptionUtils';
import { useStatusInternationalization } from '../hooks/useStatusInternationalization';
import SetReturnStatusModal, {SetReturnStatusData} from "../../../../components/SetReturnStatusModal";
import {SET_RETURNED_STATUS} from "../../../../graphql/mutations/setReturnedStatus";
import {
    GET_NEXT_ACTION_LIST,
    NextActionListResponse,
    NextActionListType
} from "../../../../graphql/queries/getNextActionList";
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { SiteType } from 'graphql/types/SiteType';
import BatchInputFilterItem from "../../components/BatchInputFilterItem";
import {
    GetHubListForOptionResponse,
    GET_HUB_LIST_FOR_OPTIONS
} from "../../../../graphql/queries/warehouseQueries";
import BatchMarkAsExceptionModal, { BatchMarkExceptionInput, ExceptionDictionaryType } from 'components/BatchMarkAsExceptionModal';
import { filterExceptionByUserRole } from 'config/roleExceptionConfig';
import { HubSelectFilter } from '../../../../components/filters/HubSelectFilter';
import { WarehouseSelectFilter } from '../../../../components/filters/WarehouseSelectFilter';
import { MultiClientSelectFilter } from '../../../../components/filters/MultiClientSelectFilter';
import { MultiPickupHubSelectFilter } from '../../../../components/filters/MultiPickupHubSelectFilter';
import { PrePickupHubSelectFilter } from '../../../../components/filters/PrePickupHubSelectFilter';
import { MultiParcelStatusSelectFilter } from '../../../../components/filters/MultiParcelStatusSelectFilter';
import DspSelectFilter from '../../../../components/filters/DspSelectFilter';
import { MultiDriverSelectFilter } from '../../../../components/filters/MultiDriverSelectFilter';
import { getUserRole } from '../../../../utils/auth';
import {useCurrentDsp} from '../../../../hooks/useCurrentDsp';
import { GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID } from '../../tasks/graphql/queries/getAvailableDriversByWarehouseId';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

interface ParcelFilterProps {
    filters: OrderFiltersType;
    setFilters: (filters: OrderFiltersType | ((prev: OrderFiltersType) => OrderFiltersType)) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderType[];
    warehouseId: String | null;
}

interface CustomerOption {
    value: string;
    label: string;
}

const ParcelFilter: React.FC<ParcelFilterProps> = ({
                                                       filters,
                                                       setFilters,
                                                       handleFilter,
                                                       handleResetFilters,
                                                       selectedRows,
                                                       warehouseId
                                                   }) => {
    const allPlaceholder = t('components.input.all');
    // ===== Label constants (for column alignment) =====
    const labelTracking = String(t('delivery.parcels.label.tracking'));
    const labelOrder = String(t('delivery.parcels.label.order'));
    const labelClient = String(t('delivery.parcels.label.client'));
    const labelParcelStatus = String(t('delivery.parcels.label.parcelStatus'));
    const labelDsp = 'DSP';
    const labelDriver = String(t('delivery.routes.label.driver'));

    const labelFirstSortCode = String(t('delivery.parcels.label.firstSortCode'));
    const labelSecondSortCode = String(t('delivery.parcels.label.secondSortCode'));
    const labelThirdSortCode = String(t('delivery.parcels.label.thirdSortCode'));
    const labelReceiverZipcode = String(t('delivery.parcels.label.receiverZipcode'));
    const labelPickupHub = String(t('delivery.parcels.label.pickupHub'));
    const labelPickupSiteId = String(t('central.parcels.filters.pickupSite'));
    const labelPrePickupHub = String(t('central.parcels.filters.prePickupHub'));

    const labelOrderTime = String(t('delivery.parcels.label.orderTime'));
    const labelPickupTaskDate = String(t('delivery.parcels.label.pickupTaskDate'));
    const labelDeliveredAt = String(t('delivery.parcels.label.deliveredAt'));
    const labelHubReceiveDate = String(t('delivery.parcels.label.hubReceiveDate'));
    const labelWhsReceiveDate = String(t('delivery.parcels.label.whsReceiveDate'));
    const labelLastDeliveryScanTime = String(t('delivery.parcels.label.lastDeliveryScanTime'));
    const labelLastTaskDate = String(t('delivery.parcels.label.lastTaskDate'));
    const labelLastUpdatedAt = String(t('delivery.parcels.label.lastUpdatedAt'));
    const labelReturnArrivalHubAt = String(t('delivery.parcels.label.returnArrivalHubAt'));

    const labelIsComplaint = String(t('delivery.parcels.label.isComplaint'));
    const labelPodReviewStatus = String(t('delivery.parcels.label.podReviewStatus'));
    const labelHasException = String(t('delivery.parcels.label.hasException'));
    const labelLastExceptionReason = String(t('delivery.parcels.label.lastExceptionReason'));
    const labelNextAction = String(t('delivery.parcels.label.nextAction'));
    const labelExceptionStatus = String(t('delivery.parcels.label.exceptionStatus'));
    const labelLastExceptionDate = String(t('delivery.parcels.label.lastExceptionDate'));

    // ===== Cross-block 3-column alignment (same as wms/parcels) =====
    const col1Labels = [
        labelTracking,
        labelOrder,
        labelDsp,
        labelFirstSortCode,
        labelReceiverZipcode,
        labelPrePickupHub,
        labelIsComplaint,
        labelLastExceptionReason,
        labelLastExceptionDate,
    ];
    const col2Labels = [
        labelClient,
        labelDriver,
        labelSecondSortCode,
        labelPickupHub,
        labelPodReviewStatus,
        labelNextAction,
    ];
    const col3Labels = [
        labelParcelStatus,
        labelThirdSortCode,
        labelPickupSiteId,
        labelHasException,
        labelExceptionStatus,
    ];
    const dateCol1Labels = [
        labelOrderTime,
        labelDeliveredAt,
        labelWhsReceiveDate,
        labelLastTaskDate,
        labelReturnArrivalHubAt,
    ];
    const dateCol2Labels = [
        labelPickupTaskDate,
        labelHubReceiveDate,
        labelLastDeliveryScanTime,
        labelLastUpdatedAt,
    ];

    // 二分布局的第一列需要与三分布局的第一列对齐：共享同一个 label 宽度计算结果
    const col1SpanClassName = getSmartLabelClassName([...col1Labels, ...dateCol1Labels]);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);
    const dateCol2SpanClassName = getSmartLabelClassName(dateCol2Labels);
    // 默认时间范围不写入 URL：用于 UI 默认展示
    // - undefined：代表“未初始化”，默认展示最近一周
    // - null：代表用户手动清空（点击叉号），必须保持为空，不再回填默认值
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedOrderTimeRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters: (next) => setFilters(next),
        timeKey: 'orderTime',
        hasUserEditedTimeRef: hasUserEditedOrderTimeRef,
    });

    const getEffectiveOrderTime = useCallback((
        orderTime: OrderFiltersType['orderTime']
    ): [string | null, string | null] | null => {
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2;
        return isValid ? (orderTime as [string | null, string | null]) : getLastWeekDateTimeRange();
    }, []);
    // 🔧 获取当前用户角色和 DSP 信息
    const userRoleType = getUserRole();
    const isDspManager = userRoleType === 'DSP_MANAGER';
    const { dspId: currentDspId } = useCurrentDsp();

    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);
    const [returnStatusVisible, setReturnStatusVisible] = useState(false);
    const [returnStatusLoading, setReturnStatusLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const [setReturnedStatus] = useMutation(SET_RETURNED_STATUS);
    const [generateBatchPdf] = useMutation<GenerateBatchShippingLabelPdfResponse, GenerateBatchShippingLabelPdfVariables>(GENERATE_BATCH_SHIPPING_LABEL_PDF);
    const userRole = getUserInfo()?.account?.role?.name || '';
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const [nextActionSelectData, setNextActionSelectData] = useState<NextActionListType[]>([]);
    const {data:nextActionList} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);
    const [hubOptions, setHubOptions] = useState<CustomerOption[]>([]);
    const {data:hubOptionsData} = useQuery<GetHubListForOptionResponse>(GET_HUB_LIST_FOR_OPTIONS);

    // 获取仓库信息进行数据隔离
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, siteType);

    // 🔧 DSP权限下查询司机列表（直接用 warehouseId 和 currentDspId）
    const { data: driversData } = useQuery(GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: warehouseId,
            dspId: isDspManager ? currentDspId : undefined
        },
        skip: !warehouseId || (isDspManager && !currentDspId),
        fetchPolicy: 'cache-and-network'
    });

    const availableDrivers = driversData?.getAvailableDriversByWarehouseId || [];

    // 优化的onChange函数，避免不必要的重新渲染
    // 传递 warehouseId，当清空时同时清空 sortingCodeSegment2（避免状态冲突）
    const handleWarehouseIdChange = useCallback((value: string | undefined) => {
        if (!value) {
            // 清空时，同时清空 sortingCodeSegment2 和 warehouseId
            // 必须明确设置为 undefined，这样 updateHashState 会包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
            setFilters((prev: OrderFiltersType) => ({
                ...prev,
                warehouseId: undefined,
                sortingCodeSegment2: undefined
            }));
        } else {
            // 选择时，只更新 warehouseId，sortingCodeSegment2 由 handleSecondSegmentChange 更新
            setFilters((prev: OrderFiltersType) => ({ 
                ...prev, 
                warehouseId: value
            }));
        }
    }, [setFilters]);

    // 处理2nd Sort Code选择时返回对应的Sorting-name和secondSegment
    const handleSecondSegmentChange = useCallback((secondSegment: string | undefined) => {
        if (!secondSegment) {
            setFilters((prev: OrderFiltersType) => ({
                ...prev,
                sortingCodeSegment2: undefined,
                warehouseId: undefined
            }));
        } else {
            setFilters((prev: OrderFiltersType) => ({ ...prev, sortingCodeSegment2: secondSegment }));
        }
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的Sorting-name
    const handleSortingNameChange = useCallback((sortingName: string | undefined) => {
        // 这里可以根据需要处理Sorting-name，比如存储到filters中或进行其他操作
        // 如果需要将Sorting-name存储到filters中，可以取消下面的注释
        // setFilters(prev => ({ ...prev, sortingName: sortingName }));
    }, [setFilters]);

    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();

    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id,
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        if (hubOptionsData) {
            const hubs = Array.isArray(hubOptionsData.allOptions) ? hubOptionsData.allOptions : [hubOptionsData.allOptions];
            setHubOptions(hubs);
        }
        if (nextActionList?.getNextActionList){
            setNextActionSelectData(nextActionList.getNextActionList);
        }
    }, [data,nextActionList,hubOptionsData]);

    // 处理时间变化的函数
    const handleOrderTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedOrderTimeRef.current = true;
        setFilters({...filters, orderTime: dates});
    };

    const handleResetFiltersWithDefaultTime = () => {
        hasUserEditedOrderTimeRef.current = false;
        handleResetFilters();
    };
    
    const handleHubReceiveChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, destHubArrivedAt: dates});
    };
    
    const handleWarehouseReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destWarehouseArrivedAt: dates});
    };
    
    const handleDeliverTaskChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, deliverAt: dates});
    };
    
    const handlePickupAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, receivedAtHub: dates});
    };

    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, markExceptionAt: dates});
    };

    const handleDeliveredAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliveredAt: dates});
    };

    // 复制选中行的运单号到剪贴板
    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRows || selectedRows.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const trackingNumbers = selectedRows
            .map(row => row.trackingNumber?.trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRows]);

    const handleLastDeliveryScanTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, lastDeliveryScanTime: dates});
    };

    const handleUpdatedAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, updatedAt: dates});
    };

    const handleReturnArrivalHubAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, returnArrivalHubAt: dates});
    };

    const handlePrintLabel = async () => {
        if (selectedRows.length < 1) {
            message.warning(t('delivery.parcels.message.selectPackageToPrint'));
            return;
        }

        try {
            // 批量生成一个包含多页的PDF
            const orderIds = selectedRows.map(order => parseInt(order.id));
            const { data } = await generateBatchPdf({
                variables: { orderIds }
            });

            if (data?.generateBatchShippingLabelPdf) {
                // 生成包含所有选中订单号的文件名
                const trackingNumbers = selectedRows
                    .map(order => order.trackingNumber || order.id)
                    .slice(0, 3); // 只取前3个，避免文件名过长
                const filenameSuffix = trackingNumbers.length < selectedRows.length 
                    ? `${trackingNumbers.join('_')}_and_${selectedRows.length - trackingNumbers.length}_more`
                    : trackingNumbers.join('_');
                const filename = `shipping_labels_${filenameSuffix}.pdf`;
                
                printPdfFromBase64(data.generateBatchShippingLabelPdf, filename);
                message.success(t('delivery.parcels.message.generateLabelSuccess', { selectedRowsLength: selectedRows.length }));
            }
        } catch (error: any) {
            console.error('Print tag failed:', error);
            const graphQLError = error?.graphQLErrors?.[0];
            const messageKey = graphQLError?.extensions?.messageKey;

            if (messageKey === 'errors.business.fbt.sortingOnlyBatchLabelNotSupported') {
                // 针对仅分拣订单错误，格式化 trackingNumbers
                const details = graphQLError.extensions?.details;
                const trackingNumbers = details?.trackingNumbers;
                const formattedDetails = trackingNumbers && Array.isArray(trackingNumbers)
                    ? { ...details, trackingNumbers: trackingNumbers.join(', ') }
                    : details;

                // 使用格式化后的 details 进行翻译并显示
                const errorMessage = String(t(messageKey, formattedDetails));
                message.error(errorMessage);
            } else if (messageKey) {
                // 其他有 messageKey 的错误，使用 showError
                showError(error);
            } else {
                // 兜底：无 messageKey 时使用通用文案
                message.error(t('delivery.parcels.message.generateLabelFailed'));
            }
        }
    };

    const handleMarkAsException = () => {
        if (selectedRows.length < 1) {
            message.warning(t('delivery.parcels.message.selectOneParcel'));
            return;
        }
        setBatchModalVisible(true);
    };

    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {

        try {
            setBatchMarkLoading(true);

            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || [],
                        exceptionHubId: siteType === SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId,
                        exceptionWarehouseId: siteType === SiteType.WAREHOUSE ? currentWarehouse?.id : null,
                        ...(order.exceptionSubReasons != null && order.exceptionSubReasons.length > 0 ? { exceptionSubReasons: order.exceptionSubReasons } : {})
                    },
                    refetchQueries: ['WarehouseQuery']
                })
            ))

            message.success(t('delivery.parcels.message.markExceptionSuccess'));
            setBatchModalVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error(t('delivery.parcels.message.markExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };

    const handleSetReturnStatus = () => {
        if (selectedRows.length < 1) {
            message.warning(t('delivery.parcels.message.selectOneParcel'));
            return;
        }
        // 检查是否所有选中的包裹都是退件相关状态
        if (!selectedRows.every( row =>
            row.overallStatus === 'RETURN_ARRIVED_DESTINATION_HUB' ||
            row.overallStatus === 'RETURN_STARTED'
        )){
            message.warning(t('delivery.parcels.message.selectReturnParcels'));
            return;
        }
        setReturnStatusVisible(true);
    };

    const handleSetReturnStatusConfirm = async (formData: SetReturnStatusData) => {
        try {
            setReturnStatusLoading(true);
            await setReturnedStatus({
                variables: {
                    orderIds: formData.orderIds,
                    returnedImageS3KeysJson: formData.returnedImageS3KeysJson
                }
            })
            message.success(t('delivery.parcels.message.setReturnStatusSuccess'));
            setReturnStatusVisible(false);
            handleFilter();
        } catch (error) {
            console.error('set return status failed:', error);
            const { message: errorMessage } = parseException(error);
            message.error(errorMessage);
        } finally {
            setReturnStatusLoading(false);
        }
    };

    return (
        <div className="mb-2 py-2">
            {/* Base Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.baseInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    <div className="min-w-0 col-span-3">
                        <BatchInputFilterItem
                        label={labelTracking}
                        value={filters.trackingNumbers}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                            ...filters,
                            trackingNumbers: e.target.value || undefined
                        })}
                        spanClassName={col1SpanClassName}
                        rows={2}
                        placeholder={t('delivery.components.enterMultipleTrackingNumber')}
                        />
                    </div>
                    <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelOrder}
                    value={filters.channelOrderNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, channelOrderNumber: e.target.value || undefined})}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                    </div>
                    <div className="min-w-0">
                <MultiClientSelectFilter
                    label={labelClient}
                    value={filters.channelId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, channelId: value})}
                    spanClassName={col2SpanClassName}
                    placeholder={allPlaceholder}
                />
                    </div>
                    <div className="min-w-0">
                <MultiParcelStatusSelectFilter
                    label={labelParcelStatus}
                    value={filters.overallStatus}
                    onChange={(value: string[] | undefined) => setFilters({...filters, overallStatus: value})}
                    spanClassName={col3SpanClassName}
                    placeholder={allPlaceholder}
                />
                    </div>
                    <div className="min-w-0">
                <DspSelectFilter
                    label={labelDsp}
                    value={isDspManager ? [String(currentDspId)] : filters.deliveredDspIds?.map(String)}
                    onChange={(value: string[] | undefined) => {
                        if (!isDspManager) {
                            setFilters({...filters, deliveredDspIds: value?.map(v => parseInt(v)), deliveredDspId: undefined});
                        }
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={allPlaceholder}
                    disabled={isDspManager}
                />
                    </div>
                {/* 只在 DSP 权限下显示司机筛选 */}
                {isDspManager && (
                    <div className="min-w-0">
                    <MultiDriverSelectFilter
                        label={labelDriver}
                        value={filters.driverAccountIds}
                        onChange={(value: number[] | undefined) => setFilters({...filters, driverAccountIds: value})}
                        drivers={availableDrivers}
                        spanClassName={col2SpanClassName}
                        placeholder={allPlaceholder}
                    />
                    </div>
                )}
                    {/* 占位，保持三列布局 */}
                    <div className="min-w-0"></div>
                </div>
                </div>
            </div>

            {/* Route Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.routeInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                <HubSelectFilter
                    label={labelFirstSortCode}
                    value={filters.sortingCodeSegment1}
                    onChange={(value, hubId) => {
                        // 当清空 Hub 选择时，同时清空相关字段
                        if (!value) {
                            setFilters({
                                ...filters,
                                sortingCodeSegment1: undefined,
                                hubId: undefined,
                                warehouseId: undefined
                            });
                            setSelectedHubId(undefined);
                        } else {
                            // 选择 Hub 时，同时更新 sortingCodeSegment1 和 hubId
                            setFilters({
                                ...filters,
                                sortingCodeSegment1: value,
                                hubId: hubId,
                                warehouseId: undefined // 切换 Hub 时清空 Warehouse
                            });
                            setSelectedHubId(hubId); // 保存选中的 Hub ID，用于级联过滤
                        }
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('delivery.parcels.placeholders.selectHub') || 'Select HUB'}
                />
                </div>
                <div className="min-w-0">
                <WarehouseSelectFilter
                    label={labelSecondSortCode}
                    value={filters.warehouseId}
                    onChange={handleWarehouseIdChange}
                    onSelectSortingName={handleSortingNameChange}
                    onSelectSecondSegment={handleSecondSegmentChange}
                    spanClassName={col2SpanClassName}
                    hubId={selectedHubId} // 使用 hubId 进行级联过滤（基于 parent_site_id）
                    placeholder={t('delivery.parcels.placeholders.selectWarehouse') || 'Select Warehouse'}
                />
                </div>
                <div className="min-w-0">
                <InputFilterItem
                    label={labelThirdSortCode}
                    value={filters.sortingCodeSegment3}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({...filters, sortingCodeSegment3: e.target.value || undefined})}
                    spanClassName={col3SpanClassName}
                />
                </div>


                <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelReceiverZipcode}
                    value={filters.receiverZipcodeInput || filters.receiverZipcode?.join('\n') || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        // 与 trackingNumbers 一致：直接保存原始字符串，在查询时转换为数组
                        setFilters({
                            ...filters,
                            receiverZipcodeInput: value || undefined
                        });
                    }}
                    placeholder={t('delivery.parcels.placeholders.receiverZipcode') || 'Enter multiple zipcodes, separated by line, comma or space'}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                </div>
                <div className="min-w-0">
                <MultiPickupHubSelectFilter
                    label={labelPickupHub}
                    value={filters.pickupHubId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, pickupHubId: value})}
                    spanClassName={col2SpanClassName}
                    placeholder={allPlaceholder}
                />
                </div>
                <div className="min-w-0">
                <PrePickupHubSelectFilter
                    label={labelPrePickupHub}
                    value={filters.prePickupHubName}
                    onChange={(name: string | undefined) => {
                        setFilters({ ...filters, prePickupHubName: name });
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.prePickupHub') || ''}
                />
                </div>
                <div className="min-w-0">
                <InputFilterItem
                    label={labelPickupSiteId}
                    value={
                        filters.cooperationWarehouseId !== undefined && filters.cooperationWarehouseId !== null
                            ? filters.cooperationWarehouseId.toString()
                            : ''
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const rawValue = e.target.value.trim();
                        if (rawValue === '') {
                            setFilters({ ...filters, cooperationWarehouseId: undefined });
                            return;
                        }
                        const numericValue = Number(rawValue);
                        if (!Number.isNaN(numericValue)) {
                            setFilters({ ...filters, cooperationWarehouseId: numericValue });
                        }
                    }}
                    spanClassName={col3SpanClassName}
                />
                </div>
                </div>
                </div>
            </div>

            {/* Date Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.dateInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelOrderTime}
                            value={getEffectiveOrderTime(filters.orderTime)}
                            onChange={handleOrderTimeChange}
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelPickupTaskDate}
                            value={filters.receivedAtHub}
                            onChange={handlePickupAtChange}
                            spanClassName={dateCol2SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelDeliveredAt}
                            value={filters.deliveredAt}
                            onChange={handleDeliveredAtChange}
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelHubReceiveDate}
                            value={filters.destHubArrivedAt}
                            onChange={handleHubReceiveChange}
                            spanClassName={dateCol2SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelWhsReceiveDate}
                            value={filters.destWarehouseArrivedAt}
                            onChange={handleWarehouseReceiveChange}
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelLastDeliveryScanTime}
                            value={filters.lastDeliveryScanTime}
                            onChange={handleLastDeliveryScanTimeChange}
                            spanClassName={dateCol2SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelLastTaskDate}
                            value={filters.deliverAt}
                            onChange={handleDeliverTaskChange}
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelLastUpdatedAt}
                            value={filters.updatedAt}
                            onChange={handleUpdatedAtChange}
                            spanClassName={dateCol2SpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelReturnArrivalHubAt}
                            value={filters.returnArrivalHubAt}
                            onChange={handleReturnArrivalHubAtChange}
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                </div>
                </div>
            </div>

            {/* Exception Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.exceptionInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelIsComplaint}
                    options={isComplaintOptions}
                    value={filters.isComplaint?.toString() ?? undefined}
                    onChange={(value: any) => setFilters({...filters, isComplaint: typeof value === 'string' ? (value ? (value === 'true') : undefined) : undefined})}
                    spanClassName={col1SpanClassName}
                />
                </div>
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelPodReviewStatus}
                    options={podReviewStatusOptions}
                    value={filters.podReviewStatus?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, podReviewStatus: value || undefined})}
                    spanClassName={col2SpanClassName}
                />
                </div>
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelHasException}
                    options={hasExceptionOptions}
                    value={filters.isHasBeenSetException?.toString() ?? undefined}
                    onChange={(value: any) => setFilters({...filters, isHasBeenSetException: typeof value === 'string' ? (value ? (value === 'true') : undefined) : undefined})}
                    spanClassName={col3SpanClassName}
                />
                </div>
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelLastExceptionReason}
                    options={exceptionList.filter(ex => !ex.isInternalException).map(ex => ({
                        value: ex.exceptionReasonCode,
                        label: getExceptionReasonText(ex)
                    })) || []}
                    value={filters.exceptionReasonCode ?? undefined}
                    onChange={(value: any) => setFilters({...filters, exceptionReasonCode: typeof value === 'string' ? (value || undefined) : undefined})}
                    spanClassName={col1SpanClassName}
                />
                </div>
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelNextAction}
                    options={nextActionSelectData.filter(item => item.value !== 0).map(item => ({
                        value: item.name,
                        label: getNextActionTextFromBackend(item.name)
                    }))}
                    mode="multiple"
                    value={filters.nextActions}
                    onChange={(value: string[] | undefined) => setFilters({...filters, nextActions: value, nextAction: undefined})}
                    spanClassName={col2SpanClassName}
                />
                </div>
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelExceptionStatus}
                    options={exceptionOrderStatusOptions}
                    mode="multiple"
                    maxTagCount="responsive"
                    value={filters?.exceptionStatuses?.map(String) ?? []}
                    onChange={(values: string[]) => setFilters({...filters, exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined})}
                    spanClassName={col3SpanClassName}
                />
                </div>
                <div className="min-w-0 col-span-3">
                    {/* 上次异常时间：二分布局（占下一行左半边，右侧占位保持二分结构） */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastExceptionDate}
                                value={filters.markExceptionAt}
                                onChange={handleExceptionTimeChange}
                                spanClassName={col1SpanClassName}
                                size="middle"
                            />
                        </div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
                </div>
                </div>
            </div>

            <div className="mb-4 flex justify-between">
                <div className="flex">
                    {!isDspManager && (
                        <div className="mr-2">
                            <Button type="primary" onClick={handlePrintLabel} className="w-auto min-w-24">
                                <span className="font-bold">{t('delivery.parcels.label.printLabel')}</span>
                            </Button>
                        </div>
                    )}
                    <div className="mr-2">
                        <Button type="primary" onClick={handleMarkAsException} className="w-auto min-w-24">
                            <span className="font-bold">{t('components.modal.markAsException')}</span>
                        </Button>
                    </div>
                    {/*<div className="mr-2">
                        <Button type="primary" onClick={handleSetReturnStatus}>
                            {t('components.modal.setReturnStatus')}
                        </Button>
                    </div>*/}

                </div>

                <div className="space-x-4">
                    <Button
                        type="primary"
                        onClick={copyTrackingNumbers}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                    </Button>
                    <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                        <span className="font-bold">{t('components.button.filter')}</span>
                    </Button>
                    <Button onClick={handleResetFiltersWithDefaultTime} className="w-auto min-w-24">
                        <span className="font-bold">{t('components.button.reset')}</span>
                    </Button>
                </div>
            </div>

            {/* 批量标记异常弹窗 */}
            <BatchMarkAsExceptionModal
                    visible={batchModalVisible}
                    parcels={selectedRows.map(row => ({
                        orderId: Number(row.id),
                        trackingNumber: row.trackingNumber,
                        existingImageUrls: row.exceptionImageS3UrlsJson || [],
                        exceptionHubId: row.exceptionHubId,
                        exceptionWarehouseId: row.exceptionWarehouseId
                    }))}
                    onOk={handleBatchMarkOk}
                    onCancel={() => setBatchModalVisible(false)}
                    loading={batchMarkLoading}
                    userRole={userRole}
                    exceptionSelectData={exceptionSelectData}
                />


            <SetReturnStatusModal
                visible={returnStatusVisible}
                onClose={() => setReturnStatusVisible(false)}
                onConfirm={handleSetReturnStatusConfirm}
                loading={returnStatusLoading}
                parcels={selectedRows}
            />
        </div>
    );
};

export default ParcelFilter; 